import { formatPrice, stopsLabel } from "@/components/trips/itinerary/itinerary-utils";
import { useSelectFocusFlight } from "@/hooks/useSelectFocusFlight";
import type { FareMatrix, TimeBucket } from "@/lib/types";

/* The leg's fare MATRIX as a real table: destination gateways (primary + any
   alternates) as labelled row-groups, searched dates as rows (day-before /
   target / day-after), and standardized time-of-day buckets as columns. Each
   priced cell is a tappable <button> that re-focuses the leg; the current focus
   cell is starred + ring-highlighted, the cheapest cell is accent-green, and an
   un-searched cell is a plain "—". Replaces the DayPricePlot scatter. */

interface FareMatrixTableProps {
  tripId: string;
  routeId: string;
  matrix: FareMatrix;
  /**
   * Render without the bordered card and the "Prices through the day" heading.
   * Used inside LegFaresPanel, which already supplies both — nesting a titled
   * card inside a titled pulldown is the double-chrome the panel exists to
   * remove.
   */
  bare?: boolean;
}

/** Human column header for each time-of-day bucket. */
const BUCKET_LABEL: Record<TimeBucket, string> = {
  redeye: "Red-eye",
  early: "Early",
  morning: "Morning",
  midday: "Midday",
  afternoon: "Afternoon",
  evening: "Evening",
};

/** Spoken bucket label for aria-labels ("morning" reads better than "Morning"). */
function bucketWord(b: TimeBucket): string {
  return BUCKET_LABEL[b].toLowerCase();
}

/** A row's role -> a small hint shown next to the date ("" for the target day). */
function roleHint(role: "target" | "before" | "after"): string {
  if (role === "before") return "day before";
  if (role === "after") return "day after";
  return "";
}

export default function FareMatrixTable({ tripId, routeId, matrix, bare = false }: FareMatrixTableProps) {
  const { select, pendingId } = useSelectFocusFlight(tripId, routeId);

  const buckets = matrix.timeBuckets;
  const totalCells = matrix.gateways.reduce(
    (n, g) => n + g.rows.reduce((m, r) => m + Object.keys(r.cells).length, 0),
    0,
  );
  if (totalCells === 0) return null;

  return (
    <div className={bare ? "pt-2" : "mt-3 rounded-lg border bg-muted/30"}>
      <div
        className={
          (bare ? "pb-1" : "px-3 pt-2") +
          " text-[11px] font-semibold uppercase tracking-wide text-muted-foreground"
        }
      >
        Prices through the day
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[520px] table-fixed border-collapse text-xs">
          <thead>
            <tr>
              <th
                scope="col"
                className="w-32 px-2 py-1.5 text-left font-medium text-muted-foreground"
              >
                {/* Row-label column */}
              </th>
              {buckets.map((b) => (
                <th
                  key={b}
                  scope="col"
                  className="px-1 py-1.5 text-center text-[11px] font-semibold uppercase tracking-wide text-muted-foreground"
                >
                  {BUCKET_LABEL[b]}
                </th>
              ))}
            </tr>
          </thead>
          {matrix.gateways.map((gw) => (
            <tbody key={gw.airport}>
              <tr>
                <th
                  scope="colgroup"
                  colSpan={buckets.length + 1}
                  className="px-2 pb-1 pt-2 text-left text-[11px] font-semibold text-foreground"
                >
                  {gw.city}
                  {gw.transferNote ? (
                    <span className="ml-1 font-normal text-muted-foreground">
                      · transfer
                    </span>
                  ) : null}
                </th>
              </tr>
              {gw.rows.map((row) => {
                const hint = roleHint(row.role);
                return (
                  <tr key={`${gw.airport}|${row.date}`} className="align-top">
                    <th
                      scope="row"
                      className="px-2 py-1 text-left text-xs font-normal text-muted-foreground"
                    >
                      <span className="text-foreground">{row.dateLabel}</span>
                      {hint ? (
                        <span className="ml-1 text-[10px] text-muted-foreground">
                          ({hint})
                        </span>
                      ) : null}
                    </th>
                    {buckets.map((b) => {
                      const cell = row.cells[b];
                      const key = `${gw.airport}|${row.date}|${b}`;
                      if (!cell) {
                        return (
                          <td
                            key={b}
                            className="px-1 py-1 text-center text-muted-foreground/50"
                          >
                            —
                          </td>
                        );
                      }
                      const isFocus = key === matrix.focusKey;
                      const isCheapest = key === matrix.cheapestKey;
                      const busy = pendingId === cell.flightId;
                      const priceLbl = formatPrice(cell.price) ?? `$${Math.round(cell.price)}`;
                      const ariaLabel = isFocus
                        ? `Current focus: ${priceLbl}`
                        : `Set as focus: ${priceLbl} ${bucketWord(b)} ${row.dateLabel}`;
                      const stops = stopsLabel(cell.stops);
                      return (
                        <td key={b} className="px-1 py-1 text-center">
                          <button
                            type="button"
                            onClick={() => !busy && !isFocus && select(cell.flightId)}
                            disabled={busy}
                            aria-pressed={isFocus}
                            aria-busy={busy}
                            aria-label={ariaLabel}
                            className={
                              "w-full rounded-md px-1 py-1 text-center transition-colors " +
                              (isFocus
                                ? "bg-[hsl(var(--primary))]/[0.12] ring-1 ring-[hsl(var(--primary))] "
                                : "hover:bg-muted ") +
                              (busy ? "opacity-50 " : "")
                            }
                          >
                            <span
                              className={
                                "block font-mono text-sm font-semibold tabular-nums " +
                                (isCheapest ? "text-emerald-700" : "text-foreground")
                              }
                            >
                              {isFocus ? "★ " : ""}
                              {priceLbl}
                            </span>
                            {cell.airline ? (
                              <span className="block text-[10px] text-muted-foreground">
                                {cell.airline}
                                {stops ? " · " + stops : ""}
                              </span>
                            ) : null}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          ))}
        </table>
      </div>
    </div>
  );
}
